import {useDeepMemo, usePrevious} from '@wandb/weave/hookUtils';
import _ from 'lodash';
import {useEffect, useMemo, useRef} from 'react';

import * as Filter from '../../util/filters';
import {
  useFirstNonEmptyValueWhileLoading,
  useStaleValueWhileLoading,
} from '../../util/hooks';
import {sortToOrderString} from '../../util/queryts';
import {Query} from '../../util/queryTypes';
import {Run} from '../../util/runTypes';
import {usePanelContext} from '../Panel/PanelContextProvider';
import {useSharedPanelState} from '../Panel/SharedPanelStateContext';
import {useIsRenderingInPopup} from '../Panel/SinglePanelInspectorContainer';
import {PanelPopupMode} from '../Panel/types';
import {POINT_VISUALIZATION_OPTIONS} from '../WorkspaceDrawer/Settings/types';
import {useSyncedPollingSubscriber} from './../../state/polling/hooks';
import {Range, runsLinePlotTransformQuery} from './common';
import {useOutliersState} from './outliersState/outliersQuery';
import {usePanelConfigContext} from './PanelConfigContext';
import {usePanelGroupingSettings} from './RunsLinePlotContext/usePanelGroupingSettings';
import {BucketedData, RunsLinePlotConfig} from './types';

/**
 * This is a data loader that's an ejection from how RSDQ's power runs line plots.
 * The reason we're ejecting: https://weightsandbiases.slack.com/archives/C062UKHTAHG/p1705531522596839
 */

const usePanelQuery = (
  pageQuery: Query,
  config: RunsLinePlotConfig,
  queryStepRange: Range
) => {
  const {limit: numRuns, parsedExpressions} = usePanelConfigContext();
  const transformed = runsLinePlotTransformQuery(
    pageQuery,
    config,
    queryStepRange,
    parsedExpressions,
    numRuns,
    1000,
    true
  );

  return useDeepMemo(transformed);
};

export function useGorillaOutliers(
  config: RunsLinePlotConfig,
  pageQuery: Query,
  queryStepRange: Range
): BucketedData {
  const panelQuery = usePanelQuery(pageQuery, config, queryStepRange);
  const lastPanelQuery = usePrevious(panelQuery);
  const {isGrouped} = usePanelGroupingSettings();

  const q = panelQuery.queries[0];
  const filters = JSON.stringify(Filter.toMongo(q.filters));
  const order = sortToOrderString(q.sort);
  /**
   * This is less than ideal but the references returned from the runsLinePlotTransformQuery aren't stable so using the actual value will thrash this query hard and crash the page. Because these are all JSON serializable we can use the JSON.stringify to get a stable reference as hack until solve this problem upstream.
   */
  const configKeys = JSON.stringify(panelQuery.configKeys);
  const specs = JSON.stringify(
    panelQuery.bucketedHistorySpecs ?? panelQuery.historySpecs
  );
  const summaryKeys = JSON.stringify(panelQuery.summaryKeys);

  const {lastTickedAt, stopTaskTimer} = useSyncedPollingSubscriber(
    'Gorilla Outliers Fetch'
  );

  // This block manages the state and data fetching for the useGorillaOutliers hook.
  // It handles caching, polling, and shared state synchronization for an expandable line plot
  // visualization that renders initially in the workspace but can be expanded to render in
  // its own popup. The same component is used for both rendering contexts.
  //
  // Caching Strategy:
  // - The workspace plot polls, updating the shared data.
  // - Initially, the plot in the popup reads the shared data.
  // - When the query of the expanded plot changes via zooming, it decouples from the shared data.
  // - The two instances then poll separately.
  //
  // shouldReadSharedData:
  // - Determines if the shared data should be read based on the query and popup state.
  // - Ensures that the shared data is only read during the initial load so that users can interact
  //   with the plot in the popup without affecting the workspace plot.
  //
  // shouldWriteSharedData:
  // - Determines if the shared data should be written based on the popup state and initial query.

  //#region useGorillaOutliersStateManagement
  const isPopup = useIsRenderingInPopup();

  const {queryState: sharedState, setQueryState: setSharedState} =
    useSharedPanelState();
  const {query: sharedQuery, result: sharedResult} = sharedState;

  const isFirstPanelQuery = useRef(true);
  if (isFirstPanelQuery.current) {
    isFirstPanelQuery.current =
      lastPanelQuery == null || lastPanelQuery === panelQuery;
  }

  const shouldReadSharedData = useMemo(
    () =>
      _.isEqual(sharedQuery, panelQuery) &&
      isFirstPanelQuery.current &&
      isPopup,
    [sharedQuery, panelQuery, isPopup]
  );

  const shouldWriteSharedData = !isPopup && isFirstPanelQuery.current;

  // this does not block the UI
  let outliersState = useOutliersState({
    xAxis: config.xAxis ?? '',
    metrics: panelQuery.bucketedHistorySpecs?.[0]?.keys,
  });

  if (shouldReadSharedData) {
    outliersState = sharedResult;
  }

  useEffect(() => {
    if (shouldWriteSharedData) {
      setSharedState({query: panelQuery, result: outliersState});
    }
  }, [outliersState, panelQuery, shouldWriteSharedData, setSharedState]);

  let {orderedRunIds, runsById} = outliersState;
  const {executeQuery, lastUpdated, totalRuns, loading} = outliersState;

  /* If we are loading, we want to keep the last loaded data around so that we can show a small spinner over the previous data,
  instead of showing a huge spinner that causes the plot to disappear. (See logic in layout.tsx).*/
  orderedRunIds = useStaleValueWhileLoading(orderedRunIds, loading);
  runsById = useStaleValueWhileLoading(runsById, loading);

  const originalOrderedRunIds = useFirstNonEmptyValueWhileLoading(
    orderedRunIds,
    loading
  );
  const originalRunsById = useFirstNonEmptyValueWhileLoading(runsById, loading);

  // if we are zooming out all the way, we want to quickly flip back to the original zoomed out data
  if (queryStepRange.min == null && queryStepRange.max == null && loading) {
    orderedRunIds = originalOrderedRunIds;
    runsById = originalRunsById;
  }

  const {panelPopupMode} = usePanelContext();

  const getVariables = () => ({
    currentRuns: orderedRunIds,
    configKeys: panelQuery.configKeys,
    // if grouping is enabled we need to pull all the basic information per runs in order to group the data clientside
    /**
     * When grouping is controlled through the run selector the RunSet object gets an entry added into the `grouping` field
     * If any of the run sets (there can just be one in a workspace but more in reports IIRC) have an entry then we need to group the data based on some of the run metadata (like the `group` field). It would be nice if we could only return the fields on the run that matched what was in the grouping rules, but GraphQL doesn't include controls granular enough to do that on the query.
     */
    enableBasic:
      (pageQuery.runSets ?? []).flatMap(rSet => (rSet.grouping ?? []).flat())
        .length > 0,
    enableConfig: panelQuery.configKeys != null,
    enableSummary: panelQuery.summaryKeys != null,
    entityName: pageQuery.entityName,
    filters,
    groupKeys: [],
    groupLevel: 0,
    lastUpdated,
    limit: panelQuery.page?.size || 10,
    order,
    projectName: pageQuery.projectName,
    bucketedHistorySpecs:
      panelQuery.bucketedHistorySpecs?.map(bhs => {
        /**
         * When we're grouping the specs the output lines can get huge and drive workspace latency
         * see: https://weightsandbiases.slack.com/archives/C010Y174QGH/p1727981436772759?thread_ts=1727808032.704569&cid=C010Y174QGH
         */
        if (isGrouped) {
          bhs.samples = 500;
          bhs.bins = 500;
        }
        return JSON.stringify(bhs);
      }) || [],
    summaryKeys: panelQuery.summaryKeys,
  });

  function checkQueryMode(
    isGrouped: boolean,
    isPopup: boolean,
    panelPopupMode: PanelPopupMode | undefined,
    config: RunsLinePlotConfig
  ): 'Grouped' | 'Full Resolution' | 'Default' | undefined {
    if (isGrouped) {
      return 'Grouped';
    }
    const series =
      (orderedRunIds.length === 0 ? 1 : orderedRunIds.length) *
      (config.metrics?.length ?? 1);
    if (
      series <= 5 &&
      isPopup &&
      panelPopupMode === PanelPopupMode.FULL_SCREEN
    ) {
      return 'Full Resolution';
    }
    if (!shouldReadSharedData) {
      return 'Default';
    }
    return undefined;
  }

  const maxSamples = 10000;

  const mutateVarsByMode = {
    Default: (variables: any) => variables,
    Grouped: (variables: any) => {
      variables.bucketedHistorySpecs = variables.bucketedHistorySpecs?.map(
        (bhs: string) => {
          const bhsObj = JSON.parse(bhs);
          bhsObj.bins = 500;
          return JSON.stringify(bhsObj);
        }
      );
    },
    /**
     * When we're grouping the specs the output lines can get huge and drive workspace latency
     * see: https://weightsandbiases.slack.com/archives/C010Y174QGH/p1727981436772759?thread_ts=1727808032.704569&cid=C010Y174QGH
     */
    'Full Resolution': (variables: any) => {
      variables.bucketedHistorySpecs = variables.bucketedHistorySpecs?.map(
        (bhs: string) => {
          const bhsObj = JSON.parse(bhs);
          bhsObj.samples = maxSamples;
          bhsObj.bins = maxSamples;
          return JSON.stringify(bhsObj);
        }
      );
    },
  };

  // these changes to the query need to fully refetch or things like the
  // legendWarning will show up wrong in the interim period
  useEffect(
    () => {
      const mode = checkQueryMode(isGrouped, isPopup, panelPopupMode, config);
      if (mode) {
        const variables = getVariables();
        mutateVarsByMode[mode](variables);
        executeQuery(variables, true);
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      panelQuery.page?.size,
      order,
      filters,
      specs,
      configKeys,
      summaryKeys,
      shouldReadSharedData,
    ]
  );

  // this is the polling query, we want this to run invisibly in the background
  useEffect(
    () => {
      if (!shouldReadSharedData) {
        executeQuery(getVariables(), false)?.finally(stopTaskTimer);
      }
    },
    // we only want to fire this on ticker changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastTickedAt]
  );
  //#endregion

  const dataMemo = useMemo(() => {
    return {
      _dataType: POINT_VISUALIZATION_OPTIONS.BucketingGorilla,
      entityName: pageQuery.entityName,
      histories: {
        data: orderedRunIds.map(id => runsById[id]).filter(r => !!r),
        keyInfo: {
          keys: {},
          sets: [],
        },
      },
      initialLoading: false,
      keyInfo: {},
      loadMore: () => {},
      projectName: pageQuery.projectName,
      totalRuns,
    };
  }, [
    pageQuery.entityName,
    pageQuery.projectName,
    orderedRunIds,
    runsById,
    totalRuns,
  ]);

  /**
   * The return here is broken up into two memos:
   * 1: loading - this property toggles constantly w/ polling. We need to have this trigger
   * rerenders so that we can show the little spinner when data is updating
   * 2: dataMemo - the catch is that we don't want to slip in a new object literal for the
   * underlying data when loading is flipping back and forth since MOST of the time no new data
   * will be returned. If this reference gets updated even though the underlying data hasn't
   * changed it can kick off a bunch of expensive downstream re-renders across the panels.
   */

  return useMemo((): BucketedData => {
    return {
      data: dataMemo,
      loading,
    };
  }, [dataMemo, loading]);
}

/**
 * Possible states of runs scanned from Big Query
 * crashed, failed, finished, killed, pending, preempted, preempting, running
 *
 * Only running is active so when runs are running we up the polling so that we are streaming fresh data in the plots
 */

export const hasActiveRuns = (
  runs: Array<Pick<Run, 'state'>>,
  activeStates = ['running']
) => {
  return runs.some(r => activeStates.includes(r.state ?? ''));
};
